import React, { useMemo, useState } from 'react';
import Tabs from '@/components/Functional/Tabs';
import IFrame from 'components/Blocks/IFrame';
import { ComponentProps } from 'lib/component-props';
import { Field } from '@sitecore-jss/sitecore-jss-nextjs';
import { useSitecoreContext } from 'hooks/useSitecoreContext';
import { useI18n } from 'next-localization';
import PropertyListingCard, { PropertyData } from 'components/Tiles/PropertyListingCard';

type Fields = {
  mapUrl: Field<string>;
  items: Array<{
    id: string;
    fields: {
      title: Field<string>;
      items: Array<{
        id: string;
        fields: PropertyData;
      }>;
    };
  }>;
};

type Props = ComponentProps & {
  fields: Fields;
};

const PropertyListingCards: React.FC<Props> = ({ fields, rendering, params }) => {
  const [activeTab, setActiveTab] = useState<number>(0);
  const {
    sitecoreContext: { language },
  } = useSitecoreContext();
  const { t } = useI18n();

  const handleToggleTab = (tab: number) => {
    setActiveTab(tab);
  };

  const tabsFields: Fields = {
    ...fields,
    items: fields.mapUrl.value
      ? [
          ...fields.items,
          { id: 'propertyMap', fields: { title: { value: t('propertyMapTitle') }, items: [] } },
        ]
      : fields.items,
  };

  const currentTab = fields.items[activeTab];
  const sortedPropertyCards = useMemo(
    () =>
      currentTab.fields.items.toSorted((a, b) =>
        a.fields.title.value.localeCompare(b.fields.title.value, language, {
          sensitivity: 'base',
        })
      ),
    [currentTab.fields.items, language]
  );

  return (
    <section
      id={rendering.uid}
      data-e2e-component-uid={rendering.uid}
      className="background-white pb-1 pb-md-2 pb-xl-4"
    >
      <Tabs activeTab={activeTab} fields={tabsFields} onTabChange={handleToggleTab} />

      <div key={currentTab.id} className="container">
        <div className="row pt-5 pt-md-6 pt-xl-8">
          {sortedPropertyCards.map((card) => (
            <PropertyListingCard
              key={card.id}
              fields={card.fields}
              className="col-12 col-md-6 col-lg-3 mb-4"
              category={currentTab.fields.title.value}
            />
          ))}
        </div>
      </div>

      {fields.mapUrl?.value && activeTab === fields.items.length && (
        <IFrame
          fields={{
            source: { value: fields.mapUrl.value },
          }}
          rendering={rendering}
          params={params}
        />
      )}
    </section>
  );
};

export default PropertyListingCards;
